import { ApiUserResumeCommentOk } from '@type/v1-api-types/user';
import { useState } from 'react';
import { Button, Modal } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSwrUserResumeComments } from '@hooks/useSwrApi/endpoints/user';
import { useBjModal } from '@hooks/common/useBjModal';
import { useAppSelector } from '@store/index';
import { clsx } from 'clsx';
import { EditCommentModalContent } from '../partials/modals/EditCommentModalContent';
import { CvDetailAvailableOperationsCurrentStatus, CvDetailInterface } from '../interface/CvDetailInterface';
import { CommentBlock } from '../partials/lists/CommentBlock';

interface IProps extends CvDetailInterface {
  locale: Locale,
  currentCVStatus?: CvDetailAvailableOperationsCurrentStatus,
  isCompany?: boolean,
  /** When provided, renders an "Add comment" button that opens the add-comment modal (owned by UnblockActions). */
  onAddComment?: () => void,
}

/**
 * @description Component to render the comments section.
 * @param cvDetail - the cv detail object
 * @param jobSlug - the job slug
 * @param currentCVStatus - the current cv status
 * @param isCompany - whether the current user is in Company context.
 * @param locale - the current app locale.
 * @param onAddComment - callback registered by UnblockActions that opens the add-comment modal.
 */
export const ProfileComments = ({
  cvDetail, jobSlug, currentCVStatus, locale, isCompany, onAddComment,
}: IProps) => {
  // Translation
  const { t } = useTranslation('common');

  // Helper variables
  const userSlug = cvDetail?.slug || '';
  const isLoggedInUserPrivateResume = cvDetail?.isOwnCV;

  // States
  const [editedComment, setEditedComment] = useState<ApiUserResumeCommentOk>();


  // Modal
  // ******************************************************
  const { isOpen, openModal, closeModal } = useBjModal();

  const shouldFetch = Boolean(useAppSelector((state) => state?.user?.headerInfo));


  // SWR: Get user comments. Pass the cvDetail?.comments as fallback data.
  // If cvDetail?.comments is undefined, then auto-fetch the data.
  // ******************************************************
  const {
    data: commentsList,
    mutate: getCommentsList,
  } = useSwrUserResumeComments({
    locale, userSlug, serverSideData: cvDetail?.comments, shouldFetch,
  });

  // Render component
  // ******************************************************
  return (
    <>
      <section className={`w-full pt-2 ${isLoggedInUserPrivateResume ? 'pointer-events-none opacity-60' : ''}`}>

        {/* Remove BIA comments from comments list */}
        {
          (commentsList ?? [])
            .filter((comment) => comment.ownerName !== 'BIA')
            .map((item) => (
              <CommentBlock
                key={item.id}
                comment={item}
                locale={locale}
                successCallback={() => {
                  void getCommentsList();
                }}
                openModal={openModal}
                editedCommentSetter={setEditedComment}
                jobSlug={jobSlug}
                currentCVStatus={currentCVStatus}
              />
            ))
        }

        {/* Add comment button — lives next to the comments it creates. The modal
            itself is owned by UnblockActions, which registers onAddComment on mount. */}
        {isCompany && onAddComment && (
          <div className="mt-2 flex">
            <Button
              styling="none"
              onClick={onAddComment}
              className={clsx(
                'inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-medium text-ink transition-all duration-200',
                'bg-surface-100 hover:bg-surface-200 hover:shadow-md',
              )}
            >
              {t('cv.action.plus.comment')}
            </Button>
          </div>
        )}

      </section>

      {/* Edit comment modal */
        editedComment && (
          <Modal
            isOpen={isOpen}
            header={t('cv.action.comment.edit')}
            body={(
              <EditCommentModalContent
                editedComment={editedComment}
                locale={locale}
                successCallback={() => {
                  void getCommentsList();
                }}
                closeModalCallback={closeModal}
              />
            )}
            onClose={closeModal}
          />
        )
      }
    </>
  );
};
